import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { DateTime } from 'luxon';

import useLogisticsApi from '../../hooks/use-logistics-api';
import { useProcessedRoutes } from '../../hooks/use-processed-routes';
import { useQueryBridge } from '../../hooks/use-query-bridge';
import { useCourierRoutesQuery } from '../../hooks/use-queries';

import OrderCard from '../../components/Card/OrderCard';
import RouteMap from '../../components/RouteMap';
import { ActionBar, ActionButton } from '../../components/Shared/ActionBar/ActionBar';

import './Courier.scss';
import { ProcessedOrder } from '../../types/logistics';
import { FaWarehouse, FaMapMarkerAlt, FaRoute, FaExclamationTriangle, FaLocationArrow, FaUndo, FaGripVertical } from 'react-icons/fa';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent, DragStartEvent, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type StopFilter = 'all' | 'collection' | 'delivery';

// Sortable wrapper for OrderCard in drag-and-drop mode
const SortableOrderCard = ({ stop, index, dragRef }: { stop: ProcessedOrder; index: number; dragRef: React.RefObject<boolean> }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: `${stop.type}-${stop.id}`,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative' as const,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div
                className="drag-handle"
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
            >
                <FaGripVertical />
            </div>
            <OrderCard data={{ ...stop, index }} dragRef={dragRef} />
        </div>
    );
};

const Courier = () => {
    // Tab state lives in the URL so a refresh / share keeps the user on map.
    // Default is map per the Track E brief — list is the secondary tab on
    // phone (where touch-and-hold drag-to-reorder is the primary UX).
    const [searchParams, setSearchParams] = useSearchParams();
    const tabShown = searchParams.get('view') === 'list' ? 'list' : 'map';
    const setTabShown = useCallback(
        (next: 'map' | 'list') => {
            setSearchParams(
                (prev) => {
                    const sp = new URLSearchParams(prev);
                    sp.set('view', next);
                    return sp;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );
    const [stopFilter, setStopFilter] = useState<StopFilter>('all');
    const [isManualOrder, setIsManualOrder] = useState(false);
    const [manualStops, setManualStops] = useState<ProcessedOrder[]>([]);

    // Drag state tracking — ref is synchronous so click handlers see it immediately
    const dragRef = useRef(false);
    const [activeDragId, setActiveDragId] = useState<string | null>(null);

    // Drag-and-drop sensors — only activate from the drag handle (via setActivatorNodeRef)
    const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 10 } });
    const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } });
    const sensors = useSensors(pointerSensor, touchSensor);

    const navigate = useNavigate();

    const { saveRouteOrder } = useLogisticsApi();

    // Shared hook: processes raw route data into UI-ready format (memoized, cache-deduplicated)
    const {
        stops, load, optimizedRoute, liveNavUrl,
        warehouseInfo, stopOrder, isLoading, error, hasData, today
    } = useProcessedRoutes();

    // Coordinates side-channel for the new map view. Hits the same React
    // Query cache key as `useProcessedRoutes` so this is a free read; no
    // extra API call.
    const { data: rawRouteData } = useCourierRoutesQuery(today);
    const orderCoordinates = rawRouteData?.orderCoordinates ?? null;

    // Restore manual stop order: server wins, then localStorage fallback
    const manualOrderRestored = useRef(false);
    useMemo(() => {
        if (stops.length === 0) return;
        if (manualOrderRestored.current) return;
        manualOrderRestored.current = true;

        const savedOrder = stopOrder
            || JSON.parse(localStorage.getItem(`manual-route-${today}`) || 'null');
        if (savedOrder && Array.isArray(savedOrder) && savedOrder.length > 0) {
            const stopMap = new Map(stops.map(s => [`${s.type}-${s.id}`, s]));
            const restored = savedOrder
                .map((id: string) => stopMap.get(id))
                .filter((s): s is ProcessedOrder => s != null);
            if (restored.length > 0) {
                setManualStops(restored);
                setIsManualOrder(true);
            }
        }
    }, [stops, stopOrder, today]);

    // Bridge loading/error to global overlay
    useQueryBridge(isLoading, error, 'Loading your route...', 'Failed to load route', hasData);

    const tabChange = (event: any) => {
        const tab = event.target.id === 'list' ? 'list' : 'map';
        setTabShown(tab);
    };

    // Filter stops by type
    const collectionCount = stops.filter(s => s.type === 'collection').length;
    const deliveryCount = stops.filter(s => s.type === 'delivery').length;

    const filteredStops = useMemo(() => {
        if (stopFilter === 'collection') return stops.filter(s => s.type === 'collection');
        if (stopFilter === 'delivery') return stops.filter(s => s.type === 'delivery');
        return stops;
    }, [stops, stopFilter]);

    // Determine which stops to render: manual order with current filter applied on top.
    // manualStops stores the full custom order; we filter it to respect the active tab.
    const displayStops = useMemo(() => {
        if (!isManualOrder) return filteredStops;
        if (stopFilter === 'all') return manualStops;
        if (stopFilter === 'collection') return manualStops.filter(s => s.type === 'collection');
        if (stopFilter === 'delivery') return manualStops.filter(s => s.type === 'delivery');
        return manualStops;
    }, [isManualOrder, manualStops, filteredStops, stopFilter]);

    const handleDragStart = useCallback((event: DragStartEvent) => {
        dragRef.current = true;
        setActiveDragId(String(event.active.id));
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragId(null);

        if (!over || active.id === over.id) {
            requestAnimationFrame(() => { dragRef.current = false; });
            return;
        }

        // The visible list is displayStops (which may be filtered).
        // Reorder within the visible subset, then merge back into the full list.
        const visible = [...displayStops];
        const oldIndex = visible.findIndex(s => `${s.type}-${s.id}` === active.id);
        const newIndex = visible.findIndex(s => `${s.type}-${s.id}` === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            requestAnimationFrame(() => { dragRef.current = false; });
            return;
        }

        const reorderedVisible = arrayMove(visible, oldIndex, newIndex);

        // Build full list: start from previous full order (or all stops),
        // replace the filtered subset with the reordered version in-place.
        const fullBase = isManualOrder ? manualStops : stops;
        let newFull: ProcessedOrder[];
        if (stopFilter === 'all') {
            // No filter active — the visible list IS the full list
            newFull = reorderedVisible;
        } else {
            // Filtered: splice reordered items back into their positions in the full list
            const reorderedIds = new Set(reorderedVisible.map(s => `${s.type}-${s.id}`));
            newFull = [];
            let ri = 0;
            for (const stop of fullBase) {
                const key = `${stop.type}-${stop.id}`;
                if (reorderedIds.has(key)) {
                    newFull.push(reorderedVisible[ri++]);
                } else {
                    newFull.push(stop);
                }
            }
        }

        setManualStops(newFull);
        setIsManualOrder(true);

        // Persist full order to localStorage (offline fallback) and server
        const today = DateTime.now().toFormat('yyyy-MM-dd');
        const orderIds = newFull.map(s => `${s.type}-${s.id}`);
        localStorage.setItem(`manual-route-${today}`, JSON.stringify(orderIds));
        saveRouteOrder(today, orderIds).catch(() => {});

        requestAnimationFrame(() => { dragRef.current = false; });
    }, [displayStops, isManualOrder, manualStops, stops, stopFilter, saveRouteOrder]);

    const handleDragCancel = useCallback(() => {
        setActiveDragId(null);
        requestAnimationFrame(() => { dragRef.current = false; });
    }, []);

    const handleResetOrder = useCallback(() => {
        setIsManualOrder(false);
        setManualStops([]);
        const today = DateTime.now().toFormat('yyyy-MM-dd');
        localStorage.removeItem(`manual-route-${today}`);
        // Save the optimized order to the server so it doesn't restore the old custom order on refresh
        const optimizedIds = filteredStops.map(s => `${s.type}-${s.id}`);
        if (optimizedIds.length > 0) {
            saveRouteOrder(today, optimizedIds).catch(() => {});
        }
    }, [filteredStops, saveRouteOrder]);

    const todayFormatted = DateTime.now().toFormat('EEEE, d MMMM yyyy');

    return (
        <>
            <div className="page-date-header">
                <span className="date-label">Today's Routes</span>
                <span className="date-value">{todayFormatted}</span>
            </div>
            <ul className="tabs" role="tablist" aria-label="Route view">
                <li
                    id="map"
                    role="tab"
                    aria-selected={tabShown === 'map'}
                    className={tabShown === 'map' ? 'active' : ''}
                    onClick={tabChange}
                >
                    Map
                </li>
                <li
                    id="list"
                    role="tab"
                    aria-selected={tabShown === 'list'}
                    className={tabShown === 'list' ? 'active' : ''}
                    onClick={tabChange}
                >
                    List
                </li>
            </ul>
            {tabShown === 'list' && stops.length > 1 && (collectionCount > 0 && deliveryCount > 0) && (
                <div className="stop-filter-bar">
                    <button className={`filter-pill ${stopFilter === 'all' ? 'active' : ''}`} onClick={() => setStopFilter('all')}>
                        All ({stops.length})
                    </button>
                    <button className={`filter-pill ${stopFilter === 'collection' ? 'active' : ''}`} onClick={() => setStopFilter('collection')}>
                        Collections ({collectionCount})
                    </button>
                    <button className={`filter-pill ${stopFilter === 'delivery' ? 'active' : ''}`} onClick={() => setStopFilter('delivery')}>
                        Deliveries ({deliveryCount})
                    </button>
                </div>
            )}
            {tabShown === 'list' && warehouseInfo && stops.length > 0 && (
                <div className="warehouse-info-card">
                    <FaWarehouse className="warehouse-icon" />
                    <div className="warehouse-details">
                        <strong>{warehouseInfo.name}</strong>
                        <span>{warehouseInfo.address}{warehouseInfo.postcode ? `, ${warehouseInfo.postcode}` : ''}</span>
                    </div>
                </div>
            )}
            {tabShown === 'list' && isManualOrder && (
                <div className="manual-order-bar">
                    <span>Custom order active</span>
                    <button onClick={handleResetOrder}><FaUndo /> Reset to Optimized</button>
                </div>
            )}
            {tabShown === 'list' &&
                <section>
                    {stops.length === 0 && (
                        <div className="empty-state">
                            <p>No stops scheduled for today.</p>
                        </div>
                    )}
                    <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onDragCancel={handleDragCancel}
                        >
                            <SortableContext
                                items={displayStops.map(s => `${s.type}-${s.id}`)}
                                strategy={verticalListSortingStrategy}
                            >
                                {displayStops.map((stop: ProcessedOrder, index: number) => (
                                    <SortableOrderCard key={`${stop.type}-${stop.id}`} stop={stop} index={index} dragRef={dragRef} />
                                ))}
                            </SortableContext>
                            <DragOverlay>
                                {activeDragId ? (() => {
                                    const stop = displayStops.find(s => `${s.type}-${s.id}` === activeDragId);
                                    if (!stop) return null;
                                    const idx = displayStops.indexOf(stop);
                                    return (
                                        <div style={{ opacity: 0.85 }}>
                                            <OrderCard data={{ ...stop, index: idx }} />
                                        </div>
                                    );
                                })() : null}
                            </DragOverlay>
                        </DndContext>
                </section>
            }
            {tabShown === 'map' &&
                <section className="route-map-section">
                    {optimizedRoute && optimizedRoute.orderedIndices.length > 0 ? (
                        <>
                            <div className="route-summary">
                                <FaRoute className="route-icon" />
                                <span>
                                    {optimizedRoute.orderedIndices.length} stops
                                    {optimizedRoute.isOptimized && `, ~${optimizedRoute.totalDistanceKm} km`}
                                </span>
                            </div>
                            {!optimizedRoute.isOptimized && (
                                <div className="route-warning">
                                    <FaExclamationTriangle />
                                    <span>Coordinates unavailable — stops shown in default order</span>
                                </div>
                            )}
                            <RouteMap
                                stops={displayStops}
                                orderCoordinates={orderCoordinates}
                                warehouse={warehouseInfo}
                            />
                            <div className="route-actions">
                                {liveNavUrl && (
                                    <button
                                        className="maps-button navigate"
                                        onClick={() => window.open(liveNavUrl, '_blank')}
                                    >
                                        <FaLocationArrow /> Navigate
                                    </button>
                                )}
                                {(() => {
                                    const mapsUrl = optimizedRoute.googleMapsUrl || optimizedRoute.googleMapsAddressUrl;
                                    return mapsUrl ? (
                                        <button
                                            className="maps-button overview"
                                            onClick={() => window.open(mapsUrl, '_blank')}
                                        >
                                            <FaMapMarkerAlt /> View Full Route
                                        </button>
                                    ) : null;
                                })()}
                            </div>
                            <ol className="route-stop-list">
                                {stops.map((stop, idx) => (
                                    <li key={idx} className="route-stop-item">
                                        <span className="stop-number">{idx + 1}</span>
                                        <div className="stop-details">
                                            <span className="stop-type">{stop.type === 'collection' ? 'Collect' : 'Deliver'}</span>
                                            <span className="stop-address">
                                                {stop.type === 'collection' ? stop.collectionAddress : stop.deliveryAddress}
                                            </span>
                                            {stop.customer && (
                                                <span className="stop-customer">
                                                    {stop.customer.firstName} {stop.customer.lastName}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </>
                    ) : (
                        <div className="empty-state">
                            <p>No route data available.</p>
                        </div>
                    )}
                </section>
            }

            {/* Action Bar */}
            {load.boxes.length > 0 && (
                <ActionBar>
                    <ActionButton variant="primary" icon={<FaWarehouse />} onClick={() => navigate('/courier/warehouse/load')}>
                        Load ({load.boxes.length})
                    </ActionButton>
                </ActionBar>
            )}
        </>
    );
};

export default Courier;
